// InteracTransferPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { centsToDollars } from 'shared-components/src/utils/currency';
import HtmlRenderer from 'shared-components/src/components/HtmlRenderer';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CenterHeader,
  BalanceHeader,
  BalanceValue,
  BalanceFee,
  Field,
  Label,
  TextInput,
  Help,
  ErrorRow,
  Spinner,
  AlertRow,
  FormHelp,
} from './styles';
import InteracImg from '../images/Interac-transfer.png';
import {
  AlertCard,
  BackButton,
  OutlineButton,
  PrimaryButton,
  ProgressBar,
  ProgressFill,
} from '../style';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import {
  createExternalAccount,
  createExternalTransfer,
  getEtransferConfig,
  postAutoDepositCheck,
} from '../services';
import { formatIntlCurrency } from '../../../utils/helpers';
import { CheckErrorIcon, MoneyPlusIcon } from '../../../components/Icons';
import SecurityQuestionField from '../../../components/SecurityQuestionField';
import {
  validateSecurityQuestion,
  securityQuestionErrorMessage,
} from '../../../utils/securityQuestion';
import { ESCROW_ERROR_CODES, recipientErrorFeedback } from './recipientErrors';

// Capitalisation and surrounding spaces aren't real differences between two email entries.
const normalizeEmail = value => (value || '').trim().toLowerCase();

export default function InteracTransfer() {
  const navigate = useNavigate();
  const intl = useIntl();
  const {
    disbursementData = {},
    transferOptions = [],
    redeemToken,
    settransferSuccessData,
  } = useContext(DisbursementsContext);
  const { amount = 0 } = disbursementData || {};
  // State
  const [selectedFlow, setSelectedFlow] = useState('ptc'); // "ptc" | "vopay"
  const [autoDepositStatus, setAutoDepositStatus] = useState(null); // "checking" | "enabled" | "disabled" | null
  const [isCheckingAutoDeposit, setIsCheckingAutoDeposit] = useState(false);
  const [hasProcessedEmail, setHasProcessedEmail] = useState(false);
  const [currentException, setCurrentException] = useState(null); // "escrow" | "network" | "insufficient_escrow" | "details" | null
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emailConfirm: '',
    securityQuestion: '',
    securityAnswer: '',
  });
  const [externalAccount, setExternalAccount] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [ipAddress, setIpAddress] = useState('');
  const [deviceFingerprint, setDeviceFingerprint] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(false);

  const shouldShowSecurityFields = () => {
    if (selectedFlow === 'vopay') return true;
    if (selectedFlow === 'ptc') {
      return hasProcessedEmail && autoDepositStatus === 'disabled';
    }
    return false;
  };
  // Call this where you define hooks so it can close over shouldShowSecurityFields()
  const validateField = (field, value) => {
    const showSec = shouldShowSecurityFields();

    const rules = {
      name: v => {
        if (!v.trim()) return intl.messages['interac.error.name'];
        if (v.trim().length < 2)
          return intl.messages['interac.error.name.length'];
        if (/\s{2,}/.test(v))
          return intl.messages['interac.error.name.consecutiveSpaces'];
        return '';
      },

      email: v => {
        if (!v.trim()) return intl.messages['interac.error.email'];
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
          return intl.messages['interac.error.email.invalid'];
        return '';
      },

      emailConfirm: v => {
        if (!v.trim()) return intl.messages['interac.error.emailConfirm'];
        if (normalizeEmail(v) !== normalizeEmail(formData.email))
          return intl.messages['interac.error.emailConfirm.mismatch'];
        return '';
      },

      securityQuestion: v => {
        if (!showSec) return '';
        return securityQuestionErrorMessage(
          intl,
          validateSecurityQuestion(v, formData.securityAnswer)
        );
      },

      securityAnswer: v => {
        if (!showSec) return '';
        const trimmed = v.trim();
        if (!trimmed) return intl.messages['interac.error.securityAnswer'];
        if (trimmed.length < 3)
          return intl.messages['interac.error.securityAnswer.minLength'];
        if (trimmed.length > 25)
          return intl.messages['interac.error.securityAnswer.maxLength'];
        // Alphanumeric only, no spaces
        if (!/^[0-9A-Za-z]{3,25}$/.test(trimmed))
          return intl.messages['interac.error.securityAnswer.pattern'];
        return '';
      },
    };

    const getError = rules[field] || (() => '');
    const error = getError(value);

    setErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let value = formData[field];
    if (field === 'name') {
      value = value.replace(/\s+/g, ' ').trim();
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    validateField(field, value);
  };
  // Shows a rejected name or email on its field, or a banner when the error doesn't say which.
  const showRecipientErrors = err => {
    const { fields, exception } = recipientErrorFeedback(err);
    const fieldNames = Object.keys(fields);

    if (fieldNames.length) {
      setErrors(prev => ({
        ...prev,
        ...Object.fromEntries(fieldNames.map(f => [f, intl.messages[fields[f]]])),
      }));
      setTouched(prev => ({
        ...prev,
        ...Object.fromEntries(fieldNames.map(f => [f, true])),
      }));
      const firstField = document.getElementById(fieldNames[0]);
      if (firstField) firstField.focus();
    }

    if (exception) {
      setCurrentException(exception);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const checkAutoDeposit = async email => {
    if (selectedFlow !== 'ptc') return;

    setIsCheckingAutoDeposit(true);
    setAutoDepositStatus('checking');
    try {
      const externalAccResponse = await createExternalAccount(
        {
          name: formData.name,
          etransfer: {
            name: formData.name,
            email,
          },
          type: 'external_funding_account',
        },
        redeemToken
      );
      setExternalAccount(externalAccResponse.data);
      const result = await postAutoDepositCheck(
        {
          external_account_id: externalAccResponse.data.id,
        },
        redeemToken
      );
      const resultStatus = result.data.auto_deposit_enabled
        ? 'enabled'
        : 'disabled';
      setAutoDepositStatus(resultStatus);
      setIsCheckingAutoDeposit(false);
      setHasProcessedEmail(true);
    } catch (err) {
      showRecipientErrors(err);
      setIsCheckingAutoDeposit(false);
      setHasProcessedEmail(false);
    }
  };
  // Helpers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));

    if (field === 'email') {
      // A changed address has to be confirmed again.
      setFormData(prev => ({ ...prev, emailConfirm: '' }));
      setErrors(prev => ({ ...prev, emailConfirm: '' }));
      setTouched(prev => ({ ...prev, emailConfirm: false }));
    }

    if ((field === 'name' || field === 'email') && currentException === 'details') {
      setCurrentException(null);
    }

    if ((field === 'name' || field === 'email') && hasProcessedEmail) {
      // The auto-deposit check created an external account for the old details; the transfer
      // must not go to it, so check again with the new ones.
      setHasProcessedEmail(false);
      setAutoDepositStatus(null);
      setExternalAccount(null);
    }
  };

  const getRequiredFields = () => {
    const base = ['name', 'email', 'emailConfirm'];
    return shouldShowSecurityFields()
      ? [...base, 'securityQuestion', 'securityAnswer']
      : base;
  };

  const isFormValid = () => {
    // if (selectedFlow === 'ptc' && !ptcDemoSelection) return false;
    const required = getRequiredFields();
    return (
      required.every(f => {
        const v = formData[f];
        return v && !errors[f];
      }) && normalizeEmail(formData.emailConfirm) === normalizeEmail(formData.email)
    );
  };

  const validateAllFields = () => {
    const required = getRequiredFields();
    let ok = true;
    required.forEach(f => {
      const v = formData[f];
      const valid = validateField(f, v);
      if (!valid) ok = false;
    });
    setTouched(prev => {
      const t = { ...prev };
      required.forEach(f => {
        t[f] = true;
      });
      return t;
    });
    return ok;
  };

  const generateFingerprint = () => {
    const fingerprintData = [
      navigator.userAgent,
      navigator.language,
      `${window.innerWidth}x${window.innerHeight}`,
      navigator.platform,
      window.devicePixelRatio,
    ].join('-');

    let hash = 0;

    for (let i = 0; i < fingerprintData.length; i += 1) {
      hash = Math.imul(hash, 31) + fingerprintData.charCodeAt(i);
    }

    return `FP-${Math.abs(hash)}`;
  };

  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api64.ipify.org?format=json');

        if (!response.ok) {
          throw new Error(`Failed to fetch IP. Status: ${response.status}`);
        }

        const data = await response.json();
        setIpAddress(data.ip);
      } catch (e) {
        setError(e);
      }
    };

    fetchIpAddress();

    const storedFingerprint = localStorage.getItem('deviceFingerprint');
    if (storedFingerprint) {
      setDeviceFingerprint(storedFingerprint);
    } else {
      const newFingerprint = generateFingerprint();
      localStorage.setItem('deviceFingerprint', newFingerprint);
      setDeviceFingerprint(newFingerprint);
    }
  }, []);

  const handleSubmit = async () => {
    if (!validateAllFields()) return;
    setIsLoading(true);

    let payload = {
      external_account_id: externalAccount ? externalAccount.id : '',
      security_question: formData.securityQuestion || '',
      security_question_answer: formData.securityAnswer || '',
      ip_address: ipAddress,
      device_fingerprint: deviceFingerprint,
      device_fingerprint_type: 'UNIQUE_DEVICE_IDENTIFIER',
      authentication_method: 'PASSWORD',
    };

    if (selectedFlow === 'ptc') {
      if (autoDepositStatus === 'disabled') {
        payload = {
          transfer_type: 'direct_send_etransfer_push',
          ...payload,
        };
      } else {
        payload = {
          transfer_type: 'direct_send_etransfer_push',
          ...payload,
        };
      }
    }

    if (selectedFlow === 'vopay') {
      let externalAccResponse;
      try {
        externalAccResponse = await createExternalAccount(
          {
            name: formData.name,
            etransfer: {
              name: formData.name,
              email: formData.email,
            },
            type: 'external_funding_account',
          },
          redeemToken
        );
      } catch (err) {
        showRecipientErrors(err);
        setIsLoading(false);
        return;
      }
      setExternalAccount(externalAccResponse.data);
      payload = {
        amount,
        transfer_type: 'direct_send_etransfer_push',
        external_account_id: externalAccResponse.data.id,
        security_question: formData.securityQuestion || '',
        security_question_answer: formData.securityAnswer || '',
      };
    }

    try {
      const createexternaltransferresponse = await createExternalTransfer(
        payload,
        redeemToken
      );
      // Kept so the receipt can show where the money went if the response doesn't say.
      settransferSuccessData({
        ...createexternaltransferresponse.data,
        entered_email: formData.email.trim(),
      });
      navigate(`/disbursements/interac-success?autodeposit=${autoDepositStatus}`);
    } catch (err) {
      const { code } = err.error || {};
      let exceptionType = 'network';
      if (ESCROW_ERROR_CODES.includes(code)) {
        exceptionType = 'insufficient_escrow';
      } else if (code === 'escrow') {
        exceptionType = 'escrow';
      }
      setCurrentException(exceptionType);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const checkConfig = async () => {
    try {
      const response = await getEtransferConfig(redeemToken);
      setSelectedFlow(response.data.data.check_auto_deposit ? 'ptc' : 'vopay');
    } catch (error) {
      setSelectedFlow('ptc');
    }
  };

  useEffect(() => {
    if (redeemToken) {
      checkConfig();
    } else {
      navigate('/disbursements/verification');
    }
  }, [redeemToken, navigate]);

  const exceptionContent = (() => {
    const exceptionsContentList = {
      escrow: {
        icon: (
          <CheckErrorIcon
            width={24}
            height={24}
            fill="#D60000"
            style={{ flexShrink: 0 }}
          />
        ), // orange-600
        title: intl.messages['interac.transfer.unavailable'],
        message: intl.messages['interac.transfer.unavailable.message'],
        variant: 'orange',
      },
      network: {
        icon: (
          <CheckErrorIcon
            width={24}
            height={24}
            fill="#D60000"
            style={{ flexShrink: 0 }}
          />
        ), // red-600
        title: intl.messages['interac.transfer.failed'],
        message: intl.messages['interac.transfer.failed.message'],
        variant: 'red',
      },
      insufficient_escrow: {
        icon: (
          <CheckErrorIcon
            width={24}
            height={24}
            fill="#D60000"
            style={{ flexShrink: 0 }}
          />
        ),
        title: intl.messages['disbursement.error.insufficient_escrow.title'],
        message: intl.messages['disbursement.error.insufficient_escrow.message'],
        variant: 'orange',
      },
      details: {
        icon: (
          <CheckErrorIcon
            width={24}
            height={24}
            fill="#D60000"
            style={{ flexShrink: 0 }}
          />
        ),
        title: intl.messages['interac.error.details.title'],
        message: intl.messages['interac.error.details.message'],
        variant: 'red',
      },
    };
    return exceptionsContentList[currentException] || null;
  })();

  const isSecurityQAEnabled =
    selectedFlow === 'vopay' ||
    (selectedFlow === 'ptc' &&
      hasProcessedEmail &&
      autoDepositStatus === 'disabled');
  return (
    <>
      {/* Progress */}
      <ProgressBar>
        <ProgressFill className="seventy" />
      </ProgressBar>

      <Container>
        {/* Back Button */}
        <BackButton onClick={() => window.history.back()}>
          <ArrowLeft size={18} />
          {intl.messages['common.goBack']}
        </BackButton>

        {/* Exception Display */}
        {exceptionContent && (
          <AlertCard $variant={exceptionContent.variant}>
            <CardBody>
              <AlertRow>
                {exceptionContent.icon}
                <div>
                  <h3>{exceptionContent.title}</h3>
                  <p>{exceptionContent.message}</p>
                  <OutlineButton
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setCurrentException(null);
                    }}
                  >
                    {intl.messages['interac.dismiss']}
                  </OutlineButton>
                </div>
              </AlertRow>
            </CardBody>
          </AlertCard>
        )}

        {/* Heading */}
        <CenterHeader>
          <h1><HtmlRenderer html={intl.messages['interac.title']} /></h1>
        </CenterHeader>

        {/* Balance Card */}
        <Card className="green-dark">
          <CardBody style={{ textAlign: 'center' }}>
            <BalanceHeader>
              <MoneyPlusIcon size={40} />
              <h2>{intl.messages['interac.balance.title']}</h2>
            </BalanceHeader>
            <div>
              <BalanceValue>
                {formatIntlCurrency(centsToDollars(amount || 0), intl.locale)}
              </BalanceValue>
              <BalanceFee>
                {intl.messages['interac.balance.fee']}:{' '}
                {formatIntlCurrency(
                  transferOptions.find(opt => opt.type === 'etransfer').fee ||
                  0,
                  intl.locale
                )}
              </BalanceFee>
            </div>
          </CardBody>
        </Card>

        {/* Contact Details */}
        <Card style={{ border: 0 }}>
          <CardHeader>
            <CardTitle>
              <img
                src={InteracImg}
                alt="Interac"
                style={{ width: 32, height: 32, objectFit: 'contain' }}
              />
              {intl.messages['interac.contact.title']}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: '24px' }}>
            {/* Name */}
            <Field>
              <Label htmlFor="name">{intl.messages['interac.name']}</Label>
              <TextInput
                id="name"
                type="text"
                placeholder={intl.messages['interac.name.placeholder']}
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                $error={!!errors.name}
              />
              {touched.name && errors.name && (
                <ErrorRow role="alert">
                  <AlertCircle size={16} />
                  <span>{errors.name}</span>
                </ErrorRow>
              )}
            </Field>

            {/* Email */}
            <Field>
              <Label htmlFor="email">{intl.messages['interac.email']}</Label>
              <TextInput
                id="email"
                type="email"
                autoComplete="email"
                placeholder={intl.messages['interac.email.placeholder']}
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                $error={!!errors.email}
              />
              {touched.email && errors.email && (
                <ErrorRow role="alert">
                  <AlertCircle size={16} />
                  <span>{errors.email}</span>
                </ErrorRow>
              )}

              {selectedFlow === 'ptc' && isCheckingAutoDeposit && (
                <ErrorRow style={{ color: '#005D1F' }}>
                  <Spinner />
                  <span style={{ color: '#005D1F' }}>
                    {intl.messages['interac.checkingAutoDeposit']}
                  </span>
                </ErrorRow>
              )}

              {autoDepositStatus === 'enabled' && (
                <Help style={{ color: '#005D1F' }}>
                  {intl.messages['interac.autodeposit.enabled']}
                </Help>
              )}

              {autoDepositStatus === 'disabled' && (
                <ErrorRow style={{ color: '#6b7280' }}>
                  <AlertCircle size={16} />
                  <span>{intl.messages['interac.autodeposit.disabled']}</span>
                </ErrorRow>
              )}
            </Field>

            {/* Confirm Email */}
            <Field>
              <Label htmlFor="emailConfirm">
                {intl.messages['interac.emailConfirm']}
              </Label>
              <TextInput
                id="emailConfirm"
                type="email"
                autoComplete="email"
                placeholder={intl.messages['interac.emailConfirm.placeholder']}
                value={formData.emailConfirm}
                onChange={e => handleInputChange('emailConfirm', e.target.value)}
                onBlur={() => handleBlur('emailConfirm')}
                $error={!!errors.emailConfirm}
              />
              {touched.emailConfirm && errors.emailConfirm && (
                <ErrorRow role="alert">
                  <AlertCircle size={16} />
                  <span>{errors.emailConfirm}</span>
                </ErrorRow>
              )}
            </Field>

            {/* Security Question */}
            {isSecurityQAEnabled && (
              <Field>
                <Label htmlFor="securityQuestion">
                  {intl.messages['interac.security.question']} *
                </Label>
                <SecurityQuestionField
                  id="securityQuestion"
                  value={formData.securityQuestion}
                  onChange={question => handleInputChange('securityQuestion', question)}
                  onBlur={() => handleBlur('securityQuestion')}
                  error={
                    touched.securityQuestion && errors.securityQuestion ? (
                      <>
                        <AlertCircle size={16} />
                        <span>{errors.securityQuestion}</span>
                      </>
                    ) : null
                  }
                  invalidProps={{ $error: !!errors.securityQuestion }}
                  inputComponent={TextInput}
                  helpComponent={Help}
                  errorComponent={ErrorRow}
                />
              </Field>
            )}

            {/* Security Answer */}
            {isSecurityQAEnabled && (
              <Field>
                <Label htmlFor="securityAnswer">
                  {intl.messages['interac.security.answer']} *
                </Label>
                <TextInput
                  id="securityAnswer"
                  type="text"
                  placeholder={
                    intl.messages['interac.security.answer.placeholder']
                  }
                  value={formData.securityAnswer}
                  onChange={e => {
                    handleInputChange('securityAnswer', e.target.value);
                  }}
                  onBlur={() => handleBlur('securityAnswer')}
                  $error={!!errors.securityAnswer}
                />
                {touched.securityAnswer && errors.securityAnswer && (
                  <ErrorRow>
                    <AlertCircle size={16} />
                    <span>{errors.securityAnswer}</span>
                  </ErrorRow>
                )}
              </Field>
            )}
          </CardBody>
        </Card>

        {/* Trademark Disclaimer */}
        <div style={{ margin: '1rem 0', fontSize: '0.875rem', color: '#555' }}>
          <p><HtmlRenderer html={intl.messages['transfer.trademark.disclaimer.interac']} /></p>
        </div>

        {/* Submit */}
        <div
          style={{
            textAlign: 'center',
            padding: '0 1rem',
            margin: '2rem 0 3rem 0',
          }}
        >
          <PrimaryButton
            onClick={() => {
              if (selectedFlow === 'ptc' && !hasProcessedEmail) {
                if (validateAllFields()) checkAutoDeposit(formData.email);
              } else {
                handleSubmit();
              }
            }}
            disabled={!isFormValid() || isCheckingAutoDeposit || isLoading}
          >
            {isCheckingAutoDeposit
              ? intl.messages['interac.checking']
              : intl.messages['interac.submitTransfer']}
          </PrimaryButton>

          {(!isFormValid() || isCheckingAutoDeposit) && (
            <FormHelp>
              {isCheckingAutoDeposit
                ? intl.messages['interac.waitAutoDeposit']
                : intl.messages['interac.completeRequiredFields']}
            </FormHelp>
          )}
        </div>
      </Container>
    </>
  );
}
